/* Welcome — post-payment password setup page.
   Stripe Checkout redirects here with ?session_id=cs_… on success.
   We use session_id as the proof-of-payment to gate first-time password creation.
   On success, we set a JWT session and redirect to /member/brief (so they can
   submit the simplified subscriber brief), or to /member if no brief is needed. */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiSetPasswordAfterCheckout, isStrongPassword, PASSWORD_HINT, formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Welcome() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const sessionId = params.get("session_id") || "";
    const setupToken = params.get("setup_token") || "";
    const productHint = params.get("product") || ""; // "kit" | "subscription" (optional)

    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [prefillEmail, setPrefillEmail] = useState("");
    const [tokenError, setTokenError] = useState("");

    useEffect(() => {
        if (!sessionId && !setupToken) {
            toast.error("Missing setup link — please use the link from your email.");
            return;
        }
        // If migration link, fetch the email to display on the page
        if (setupToken) {
            (async () => {
                try {
                    const r = await axios.get(`${API}/auth/setup-token/${setupToken}`);
                    setPrefillEmail(r.data?.email || "");
                } catch (e) {
                    setTokenError(e?.response?.data?.detail || "Setup link invalid or expired");
                }
            })();
        }
    }, [sessionId, setupToken]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!sessionId && !setupToken) {
            toast.error("Missing setup link");
            return;
        }
        if (!isStrongPassword(password)) {
            toast.error(PASSWORD_HINT);
            return;
        }
        if (password !== confirmPw) {
            toast.error("Passwords don't match");
            return;
        }
        try {
            setBusy(true);
            await apiSetPasswordAfterCheckout({
                session_id: sessionId || undefined,
                setup_token: setupToken || undefined,
                password,
                name: name.trim() || undefined,
            });
            toast.success("Account ready!");
            nav("/member", { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not set password");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-cafe-cream flex flex-col">
            <header className="px-4 sm:px-6 py-4 border-b border-border">
                <Link to="/" className="font-serif text-xl text-cafe-espresso hover:text-cafe-olive" data-testid="welcome-logo-link">
                    kaily<span className="text-cafe-terracotta">.</span>
                </Link>
            </header>

            <main className="flex-1 flex items-center justify-center px-4 py-10">
                <div className="w-full max-w-md">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-5 w-5 text-cafe-olive" />
                        <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">
                            {setupToken ? "Migration link" : "Payment received"}
                        </p>
                    </div>
                    <h1 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-2">
                        Set your password.
                    </h1>
                    <p className="text-sm text-cafe-mocha mb-2">
                        {setupToken
                            ? "Pick a password so you can sign in at /member/login anytime."
                            : "Your account is being created. Pick a password so you can sign in next time."}
                    </p>
                    {prefillEmail && (
                        <p className="text-xs text-cafe-olive mb-6" data-testid="welcome-prefill-email">
                            For: <strong>{prefillEmail}</strong>
                        </p>
                    )}
                    {tokenError && (
                        <p className="text-xs text-cafe-terracotta mb-6" data-testid="welcome-token-error">
                            {tokenError}
                        </p>
                    )}
                    {!prefillEmail && !tokenError && <div className="mb-6" />}

                    <form onSubmit={submit} className="space-y-4" data-testid="welcome-form">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1.5 block">Your name (optional)</span>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Kelly"
                                className="w-full border border-border focus:border-cafe-olive rounded-xl px-3.5 py-2.5 text-sm bg-white outline-none"
                                data-testid="welcome-name-input"
                            />
                        </label>

                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1.5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Password</span>
                            <input
                                type="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 8 chars, with a letter + digit"
                                className="w-full border border-border focus:border-cafe-olive rounded-xl px-3.5 py-2.5 text-sm bg-white outline-none"
                                data-testid="welcome-password-input"
                            />
                            <p className="text-[11px] text-cafe-mocha mt-1.5">{PASSWORD_HINT}</p>
                        </label>

                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1.5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Confirm password</span>
                            <input
                                type="password"
                                autoComplete="new-password"
                                value={confirmPw}
                                onChange={(e) => setConfirmPw(e.target.value)}
                                placeholder="Re-enter your password"
                                className="w-full border border-border focus:border-cafe-olive rounded-xl px-3.5 py-2.5 text-sm bg-white outline-none"
                                data-testid="welcome-confirm-input"
                            />
                        </label>

                        <button
                            type="submit"
                            disabled={busy || (!sessionId && !setupToken)}
                            className="w-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="welcome-submit-btn"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="h-4 w-4" /></>}
                        </button>

                        {productHint && (
                            <p className="text-[11px] text-cafe-mocha text-center italic">
                                Your {productHint === "subscription" ? "subscription" : "kit"} purchase is confirmed.
                            </p>
                        )}
                    </form>
                </div>
            </main>
        </div>
    );
}
